<template>
    <div
        v-if="showHeader"
        class="ml-2 hidden lg:inline-block"
    >
        <div class="flex items-center gap-6">
            <div class="haptik">Lead #{{ leadSale.order.ref }}</div>
            <span
                v-if="isMoveproManaged"
                class="flex items-center gap-1.5 text-sm text-muval-gray-3"
            >
                <SvgIcon
                    name="large-Lock"
                    class="h-4 w-4"
                />
                Managed in Movepro
            </span>
            <MuvalSelect
                v-else
                :model-value="leadSale.status"
                class="w-filter"
                :single-select="true"
                :rounded="true"
                label="Mark lead as"
                placeholder="Status"
                :selectOptions="statusOptions"
                highlightOnValue
                :cannotDeselect="true"
                @change="updateLeadSaleStatus($event)"
            />
            <button
                type="button"
                class="flex cursor-pointer items-center gap-1 text-sm font-normal"
                @click="openMoveproImport"
            >
                <span class="text-[#1F3DF0]">{{ moveproImportLabel }}</span>
                <SvgIcon
                    name="system-upload"
                    class="h-4 w-4 text-[#1F3DF0]"
                />
            </button>
        </div>
    </div>

    <div
        v-if="showHeader"
        class="lg:hidden"
    >
        <div class="flex flex-col gap-6">
            <div class="haptik">{{ `Lead #${leadSale.order.ref}` }}</div>
            <div class="flex items-center gap-6">
                <span
                    v-if="isMoveproManaged"
                    class="flex items-center gap-1.5 text-sm text-muval-gray-3"
                >
                    <SvgIcon
                        name="large-Lock"
                        class="h-4 w-4"
                    />
                    Managed in Movepro
                </span>
                <MuvalSelect
                    v-else
                    v-model="leadSale.status"
                    class="w-filter"
                    :single-select="true"
                    :rounded="true"
                    label="Mark lead as"
                    placeholder="Status"
                    :selectOptions="statusOptions"
                    highlightOnValue
                />
                <button
                    type="button"
                    class="flex cursor-pointer items-center gap-1 text-sm font-normal"
                    @click="openMoveproImport"
                >
                    <span class="text-[#1F3DF0]">{{ moveproImportLabel }}</span>
                    <SvgIcon
                        name="system-upload"
                        class="h-4 w-4 text-[#1F3DF0]"
                    />
                </button>
            </div>
        </div>
    </div>

    <MoveproImportModals
        ref="moveproModals"
        :enabled="isMoveproEnabled"
        :dayjs="dayjs"
    />
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { useLeadSaleStore } from '@store';
import { notify } from '@kyvg/vue3-notification';
import MoveproImportModals from '@components/leads/MoveproImportModals.vue';

defineProps({
    title: {
        type: String,
        required: true,
    },
});

const leadSaleStore = useLeadSaleStore();
const dayjs = useDayJs();

// Pinia unwraps refs returned from setup stores, so this is already a boolean-like value.
const isMoveproEnabled = computed(() => Boolean(leadSaleStore.isMoveproEnabled));

const { leadSale } = storeToRefs(leadSaleStore);

const moveproModals = ref(null);

const showHeader = computed(() => {
    // Avoid optional chaining inside template expressions for build compatibility.
    return Boolean(leadSale.value && leadSale.value.order && leadSale.value.order.ref);
});

// Once a lead is pushed to Movepro its status syncs back from there and is read-only in
// Muval (the server rejects status changes), so we lock it here too — mirroring the
// company lead list view.
const isMoveproManaged = computed(() => Boolean(leadSale.value?.movepro_pushed_at));

const statusOptions = [
    { id: 'IN_PROGRESS', name: 'In progress' },
    { id: 'WON', name: 'Won' },
    { id: 'LOST', name: 'Lost' },
];

// Manual bypass to the automatic Movepro API push. Always shown (alongside the auto push):
// for Movepro-enabled companies it opens a modal with the lead's details to copy into
// Movepro by hand; for non-enabled companies the modal doubles as a Movepro upsell. The
// enabled/not-enabled split is handled inside MoveproImportModals via :enabled.
const moveproImportLabel = 'Movepro import';

function openMoveproImport() {
    // Avoid optional-chaining directly in templates (some builds/parsers can be picky).
    moveproModals.value?.open?.(leadSale.value);
}

async function updateLeadSaleStatus(newStatus) {
    const uuid = leadSale.value.uuid;
    try {
        const response = await leadSaleStore.updateLeadSaleStatus(uuid, newStatus);
        if (response === undefined) {
            return;
        }
        const data = response?.data ?? {};

        if (data?.success === true) {
            notify({
                title: 'Success',
                text: 'Lead status updated.',
                group: 'app_success',
            });
            return;
        }

        notify({
            title: 'Error',
            text: 'Failed to update lead status.',
            group: 'app_error',
        });
    } catch (e) {
        notify({
            title: 'Error',
            text: e?.message || 'Failed to update lead status.',
            group: 'app_error',
        });
    }
}
</script>
